import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { ApiError, CompanyProfile, getCompany } from "services";
import { Tenant } from "./tenant";

type TenantState = {
  slug: string;
  company: CompanyProfile;
};

const TenantContext = createContext<TenantState | null>(null);

/** The resolved company. Only usable inside a loaded TenantProvider. */
export const useTenant = (): TenantState => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error("useTenant must be used within a TenantProvider");
  }
  return context;
};

type Props = {
  tenant: Tenant;
  children: ReactNode;
  notFound: ReactNode;
  failed: (retry: () => void) => ReactNode;
};

/**
 * Loads the company for the URL's slug before rendering the portal. An
 * unknown slug shows `notFound`; other failures show `failed`.
 */
export const TenantProvider = ({ tenant, children, notFound, failed }: Props) => {
  const [state, setState] = useState<
    | { status: "loading" }
    | { status: "ready"; company: CompanyProfile }
    | { status: "not-found" }
    | { status: "error" }
  >({ status: tenant.slug ? "loading" : "not-found" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!tenant.slug) {
      setState({ status: "not-found" });
      return;
    }
    let cancelled = false;
    setState({ status: "loading" });
    getCompany(tenant.slug)
      .then((company) => {
        if (cancelled) return;
        setState({ status: "ready", company });
        if (company.name) document.title = `Careers at ${company.name}`;
      })
      .catch((error) => {
        if (cancelled) return;
        setState({
          status:
            error instanceof ApiError && error.status === 404
              ? "not-found"
              : "error",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [tenant.slug, attempt]);

  if (state.status === "loading") {
    return (
      <div role="status" className="p-10 text-center text-gray-500">
        Loading…
      </div>
    );
  }
  if (state.status === "not-found") return <>{notFound}</>;
  if (state.status === "error") return <>{failed(() => setAttempt((n) => n + 1))}</>;

  return (
    <TenantContext.Provider
      value={{ slug: tenant.slug as string, company: state.company }}
    >
      {children}
    </TenantContext.Provider>
  );
};
